"use client"

import React from "react"

import { Card, CardContent, CardHeader, Input } from "@coval/ui"
import { SlidersHorizontal } from "lucide-react"

import {
  getIterationBlurResult,
  getIterationInputChangeResult,
  getNumericInputBlurResult,
  getNumericInputChangeResult,
  getTestSetSelectorMaxConcurrencyLimit,
  MAX_ITERATIONS,
} from "./testSetSelectorState"

interface RunConfigurationCardProps {
  iterationCount?: number
  concurrency?: number
  /** Drives the concurrency ceiling alongside the organization's own limit. */
  simulatorType?: string
  maxConcurrency?: number
  disabled?: boolean
  onIterationCountChange?: (count: number) => void
  onConcurrencyChange?: (concurrency: number) => void
}

/**
 * How the run is executed rather than what it runs against: how many times each
 * test case repeats, and how many sessions run at once. Both apply to the whole
 * launch, so they sit in their own section below the selections they scale.
 */
export function RunConfigurationCard({
  iterationCount = 1,
  concurrency = 1,
  simulatorType = "MODEL_TYPE_CHAT",
  maxConcurrency,
  disabled = false,
  onIterationCountChange,
  onConcurrencyChange,
}: RunConfigurationCardProps) {
  const maxConcurrencyLimit = getTestSetSelectorMaxConcurrencyLimit(
    simulatorType,
    maxConcurrency,
  )

  React.useEffect(() => {
    if (concurrency > maxConcurrencyLimit) {
      onConcurrencyChange?.(maxConcurrencyLimit)
    }
  }, [concurrency, maxConcurrencyLimit, onConcurrencyChange])

  const iterationDisplayValue = React.useMemo(
    () => iterationCount.toString(),
    [iterationCount],
  )

  const concurrencyDisplayValue = React.useMemo(
    () => concurrency.toString(),
    [concurrency],
  )

  const [iterationInputValue, setIterationInputValue] = React.useState(
    iterationDisplayValue,
  )
  const [concurrencyInputValue, setConcurrencyInputValue] = React.useState(
    concurrencyDisplayValue,
  )

  React.useEffect(() => {
    setIterationInputValue(iterationDisplayValue)
  }, [iterationDisplayValue])

  React.useEffect(() => {
    setConcurrencyInputValue(concurrencyDisplayValue)
  }, [concurrencyDisplayValue])

  const handleIterationInputChange = (value: string) => {
    const result = getIterationInputChangeResult({
      rawValue: value,
      concurrency,
      maxConcurrencyLimit,
    })
    setIterationInputValue(result.cleanValue)
    if (result.nextIterationCount !== undefined) {
      onIterationCountChange?.(result.nextIterationCount)
    }
    if (result.nextConcurrency !== undefined) {
      onConcurrencyChange?.(result.nextConcurrency)
    }
  }

  const handleIterationBlur = () => {
    const result = getIterationBlurResult({
      inputValue: iterationInputValue,
      concurrency,
      maxConcurrencyLimit,
    })
    if (!result) {
      return
    }
    setIterationInputValue(result.nextInputValue)
    onIterationCountChange?.(result.nextIterationCount)
    if (result.nextConcurrency !== undefined) {
      onConcurrencyChange?.(result.nextConcurrency)
    }
  }

  const handleConcurrencyInputChange = (value: string) => {
    const result = getNumericInputChangeResult(value, maxConcurrencyLimit)
    setConcurrencyInputValue(result.cleanValue)
    if (result.numericValue !== undefined) {
      onConcurrencyChange?.(result.numericValue)
    }
  }

  const handleConcurrencyBlur = () => {
    const result = getNumericInputBlurResult(
      concurrencyInputValue,
      maxConcurrencyLimit,
    )
    if (!result) {
      return
    }
    setConcurrencyInputValue(result.nextInputValue)
    onConcurrencyChange?.(result.nextNumericValue)
  }

  return (
    <Card className="w-full">
      <CardHeader className="p-6 pb-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-coval-text">
          <SlidersHorizontal className="size-4 shrink-0" />
          Configuration
        </h3>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        <div className="flex items-start gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-coval-text">
              Iterations
            </label>
            <Input
              type="text"
              inputMode="numeric"
              aria-label="Iteration count"
              value={iterationInputValue}
              onChange={(e) => handleIterationInputChange(e.target.value)}
              onBlur={handleIterationBlur}
              disabled={disabled}
              className="w-16 text-center font-medium"
            />
            <p className="text-xs text-coval-text-muted">
              Max: {MAX_ITERATIONS}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-coval-text">
              Concurrent Sessions
            </label>
            <Input
              type="text"
              inputMode="numeric"
              aria-label="Concurrent sessions count"
              value={concurrencyInputValue}
              onChange={(e) => handleConcurrencyInputChange(e.target.value)}
              onBlur={handleConcurrencyBlur}
              disabled={disabled}
              className="w-16 text-center font-medium"
            />
            <p className="text-xs text-coval-text-muted">
              Max: {maxConcurrencyLimit}
            </p>
            {concurrency > maxConcurrencyLimit && (
              <p className="text-xs text-coval-danger-text">
                Exceeds maximum for this simulator
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
